import { bindActionCreators } from '@reduxjs/toolkit';
import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import QueryFilter from '../../helpers/query-filter';
import { getList as getListSelector } from '../../selectors/list';
import { requestStatus } from '../../shared/statuses';
import { getSearchPages, getSearchResultSet, getSearchSorting } from '../../utils/reselectors';

import { all as getBrandings } from '../../actions/branding';
import { history } from '../../actions/history';
import { all as getList } from '../../actions/list';
import { search } from '../../actions/search';

import DefaultFilterBlock from '../filters/default-filter-block';
import TypeFilterBar from '../filters/type-filter-bar';
import SearchGrid from './search-grid';

import { useTranslation } from 'react-i18next';
import { createPageTitle } from '../../helpers/text';
import useQueryFilterFromUrl from '../../hooks/useQueryFilterFromUrl';
import useTrackPageView from '../../hooks/useTrackPageView';
import type { RootState } from '../../reducers';
import useSeoSearch from '../../routes/customer-service/Data/useSeoSearch';
import SearchEngineOptimizationBlock from '../../routes/search/components/search-engine-optimization-block';
import ManufacturePackaging from '../../routes/search/manufacture-packaging';
import { shouldFetchBrandings as _shouldFetchBrandings } from '../../selectors/branding';
import MainSearch from '../../styled-components/structures/MainSearch';

interface SearchBodyProps {
  target: string;
  navigate: (queryFilter: QueryFilter) => void;
}

const SearchBody = ({ target, navigate }: SearchBodyProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('search');

  const isSearching = useSelector<RootState, boolean>(
    (s) => s.search.requestStatus === requestStatus.FETCHING_LIST,
  );
  const pages = useSelector(getSearchPages);
  const queryFilter = useSelector<RootState, string | null>((s) => s.search.queryFilter);
  const resultSet = useSelector(getSearchResultSet);
  const sortOptions = useSelector(getSearchSorting);
  const wasRedirect = useSelector<RootState, string | null | undefined>((s) => s.routing.from);
  const hasListPages = useSelector<RootState, number>((s) => getListSelector(s).length);
  const shouldFetchBrandings = useSelector(_shouldFetchBrandings);
  const seoOverride = useSeoSearch();
  const isSearchEngineOptimized = useSelector<RootState, boolean>((s) => s.search.initialized);
  const trackPageViewEvent = useTrackPageView();

  const qf = useQueryFilterFromUrl(target);

  const actions = bindActionCreators(
    {
      search,
      getBrandings,
      getList,
      history,
    },
    dispatch,
  );

  const timeoutRef = useRef(-1);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isSearchEngineOptimized) {
      if (!document.title.includes('Nya och begagnade')) {
        document.title = createPageTitle(t('findVehicles', { ns: 'search' }));
      }
    }
  }, [isSearchEngineOptimized]);

  useEffect(() => {
    if (
      !isSearchEngineOptimized &&
      !seoOverride &&
      !document.title.includes('Sök') &&
      !document.title.includes('Nya och begagnade')
    ) {
      document.title = createPageTitle(t('findVehicles', { ns: 'search' }));
    }

    if (shouldFetchBrandings) {
      actions.getBrandings();
    }

    if (!hasListPages) {
      actions.getList();
    }

    window.addEventListener('scroll', onPageScroll);

    return () => {
      window.removeEventListener('scroll', onPageScroll);
    };
  }, []);

  useEffect(() => {
    if (resultSet?.length) {
      trackPageViewEvent();
    }
  }, [resultSet]);

  useEffect(() => {
    const isSame = qf.toString().localeCompare(queryFilter || '') === 0;
    if (!isSearching) {
      /**
       * isSame, if false then qf in state diffs from state in url, do search
       * if isSame is true and resultSet.length is false, user was probably navigating to this page, do search
       */
      if (!isSame || !resultSet.length) {
        actions.search(qf);
      }
    } else if (!wasRedirect) {
      // Hard landing on searchpage, facets are SSR'ed and loading state is FETCHING_LIST (isSearching=true)
      actions.search(qf);
    }
  }, [qf.toString()]);

  const onPageScroll = (): void => {
    if (window.scrollY > 2000 && queryFilter) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = window.setTimeout(() => {
        actions.history(new QueryFilter(queryFilter));
      }, 250);
    }
  };

  const onRequestSearch = (qf: QueryFilter): void => {
    const nextQueryFilter = qf.removePagination();
    navigate(nextQueryFilter);
  };

  const scrollToTop = (): void => {
    const refObject = containerRef.current;
    if (refObject) {
      const scrollTarget = refObject.getBoundingClientRect();
      if (scrollTarget.top < 0) {
        document.body.scrollTop = document.documentElement.scrollTop = 0;
      }
    }
  };

  const onApplySort = (filterQuery: string): void => {
    scrollToTop();
    const nextQueryFilter = new QueryFilter(filterQuery)
      .removePagination()
      .removeDefaultSortOption();
    navigate(nextQueryFilter);
  };

  return (
    <>
      <section className="page-section page-section-thin">
        <MainSearch queryFilter={new QueryFilter(queryFilter)} />
      </section>
      <section className="page-section page-section-thin">
        <DefaultFilterBlock target={target} navigate={navigate}>
          <SearchEngineOptimizationBlock />
        </DefaultFilterBlock>
      </section>
      <TypeFilterBar
        onRequestSearch={onRequestSearch}
        sortOptions={sortOptions}
        onApplySort={onApplySort}
      />
      <ManufacturePackaging />
      <SearchGrid
        isSearching={isSearching}
        ref={containerRef}
        resultSet={resultSet}
        pages={pages}
        target={target}
        onScrollTop={scrollToTop}
      />
    </>
  );
};

export default SearchBody;
